import { Platform } from 'react-native';
import * as Sentry from '@sentry/browser';
import { routePattern, scrubEvent } from '@hogar/shared';

/**
 * Crash reports from the owner app to Sentry's app project (company standard, section 5). The key
 * comes from the cloud service at start, so no key is built into the app; without one, nothing is
 * sent and nothing breaks. No user is attached, no session replay, and every event is scrubbed.
 * The installed iPhone and Android apps will use Sentry's React Native library (milestone 6).
 */
let on = false;

export async function startMonitoring() {
  if (Platform.OS !== 'web' || on) return;
  try {
    const config = (await (await fetch('/api/client-config')).json()) as { sentryDsn: string | null; release: string | null };
    if (!config.sentryDsn) return;
    Sentry.init({
      dsn: config.sentryDsn,
      release: config.release ?? undefined,
      environment: 'production',
      // Nothing about the person: no user info, cookies, headers, bodies, or query strings.
      dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false },
      tracesSampleRate: 0,
      maxBreadcrumbs: 0,
      initialScope: { tags: { surface: 'owner-app' } },
      beforeSend: (event) => scrubEvent(event, location.pathname),
    });
    on = true;
    // Proof that reports arrive (company standard, section 11): app.dejodicho.com/#sentry-test sends one.
    if (location.hash === '#sentry-test') {
      Sentry.captureMessage('Sentry test from the owner app - safe to ignore');
      history.replaceState(null, '', location.pathname);
    }
  } catch {
    // Crash reporting never breaks the app (company standard, rule 3).
  }
}

/** A crash on a screen, under the reference the owner sees. */
export function reportAppError(error: Error, reference: string) {
  if (!on) return;
  Sentry.withScope((scope) => {
    scope.setTag('reference', reference);
    scope.setTag('screen', routePattern(location.pathname));
    Sentry.captureException(error);
  });
}
